import arbreFieldJson from "../../../../lib/inventory/data/fields-tree.json";
import alignementFieldJson from "../../../../lib/inventory/data/alignement-fields.json";
import { Step } from '../../shared/types';

const adaptValues = (values: any[]) => {
  return values.map(value => {
    return {
      ...value,
      hidden: value.hidden || false
    }
  }).filter(value => !value.hidden)
}

/**
 * Get field values from the appropriate JSON file based on inventory type
 */
function getFieldValues(fieldName: string, inventaireType?: string): any[] {
  const isAlignement = inventaireType?.toUpperCase() === 'ALIGNEMENT';
  const fieldJson = isAlignement ? alignementFieldJson : arbreFieldJson;
  const field = (fieldJson as Record<string, any>)[fieldName];
  return adaptValues(field?.values || []);
}

/**
 * Get edit steps configuration based on inventory type
 * @param inventaireType - Type of inventory ('ARBRE' or 'ALIGNEMENT'). Defaults to 'ARBRE'
 */
export function getEditSteps(inventaireType?: string): Step[] {
  return [
    {
      id: 'identification',
      title: 'Identification',
      description: 'Informations principales d\'identification de l\'arbre',
      fields: [
        { name: 'hauteur', label: 'Mesure en mètres', type: 'number', required: true, unit: 'm' },
        { name: 'diametre', label: 'Mesure à 1,3 m du sol', type: 'number', required: true, unit: 'cm' },
        {
          name: 'espece',
          label: 'Espèce',
          type: 'radio',
          required: true,
          options: [] // Will be populated dynamically from API
        },
        { name: 'code_site', label: 'Code site', type: 'text', required: false },
        { name: 'num_sujet', label: 'Numéro sujet', type: 'text', required: false }
      ],
      category: 0
    },
    {
      id: 'images',
      title: 'Images',
      description: 'Photos de l\'arbre',
      fields: [
        {
          name: 'img1',
          label: 'Image principale',
          type: 'image-upload',
          required: false,
          imageConfig: {
            maxFiles: 1,
            acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'],
            maxSize: 20
          }
        },
        {
          name: 'img2',
          label: 'Image secondaire',
          type: 'image-upload',
          required: false,
          imageConfig: {
            maxFiles: 1,
            acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'],
            maxSize: 20
          }
        },
        {
          name: 'img3',
          label: 'Image tertiaire',
          type: 'image-upload',
          required: false,
          imageConfig: {
            maxFiles: 1,
            acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'],
            maxSize: 20
          }
        }
      ],
      category: 1
    },
    {
      id: 'caracteristiques',
      title: 'Caractéristiques du sujet',
      description: 'Informations de base sur l\'arbre',
      fields: [
        {
          name: 'au-pied-de-l-arbre',
          label: 'Au pied de l\'arbre',
          type: 'multiselect',
          required: true,
          options: getFieldValues('au-pied-de-l-arbre', inventaireType)
        },
        {
          name: 'caracteristiques-du-sujet-tronc',
          label: 'Tronc',
          type: 'radio',
          required: true,
          options: getFieldValues('caracteristiques-du-sujet-tronc', inventaireType)
        },
        {
          name: 'port-de-larbre',
          label: 'Port de l\'arbre',
          type: 'radio',
          required: true,
          options: getFieldValues('port-de-larbre', inventaireType)
        },
        {
          name: 'stade-de-developpement',
          label: 'Stade de développement',
          type: 'radio',
          required: true,
          options: getFieldValues('stade-de-developpement', inventaireType)
        },
        {
          name: 'critere-remarquable',
          label: 'Critères remarquables',
          type: 'multiselect',
          required: true,
          options: getFieldValues('critere-remarquable', inventaireType)
        }
      ],
      category: 0
    },
    {
      id: 'etat-sanitaire',
      title: 'État sanitaire',
      description: 'Évaluation de la santé de l\'arbre',
      fields: [
        {
          name: 'etat-sanitaire-systme-racinaire-collet',
          label: 'Système racinaire et collet',
          type: 'multiselect',
          required: true,
          options: getFieldValues('etat-sanitaire-systme-racinaire-collet', inventaireType)
        },
        {
          name: 'etat-sanitaire-tronc',
          label: 'Tronc',
          type: 'multiselect',
          required: true,
          options: getFieldValues('etat-sanitaire-tronc', inventaireType)
        },
        {
          name: 'etat-sanitaire-houppier',
          label: 'Houppier',
          type: 'multiselect',
          required: true,
          options: getFieldValues('etat-sanitaire-houppier', inventaireType)
        },
        {
          name: 'etat-sanitaire-general',
          label: 'État physiologique',
          type: 'multiselect',
          required: true,
          options: getFieldValues('etat-sanitaire-general', inventaireType)
        },
        {
          name: 'risque-de-rupture-generale',
          label: 'Risque de rupture général',
          type: 'multiselect',
          required: true,
          options: getFieldValues('risque-de-rupture-generale', inventaireType),
        }
        , {
          name: 'risque-de-rupture',
          label: 'Risque de rupture',
          type: 'table',
          required: true,
          tableConfig: {
            "cols": [
              {
                "id": "faible",
                "label": "Faible"
              },
              {
                "id": "modere",
                "label": "Modéré"
              },
              {
                "id": "eleve",
                "label": "Elevé"
              },
              {
                "id": "imminent",
                "label": "Imminent"
              }
            ],
            rows: ["Collet", "Tronc", "Houppier"],
          }
        }
      ],
      category: 2
    },
    {
      id: 'environnement',
      title: 'Environnement',
      description: 'Contexte environnemental',
      fields: [
        {
          name: 'implantation',
          label: 'Implantation',
          type: 'radio',
          required: true,
          options: getFieldValues('implantation', inventaireType),
          // Hide this field when inventaire type is 'Alignement'

        },
        {
          name: 'domaine',
          label: 'Domaine',
          type: 'radio',
          required: true,
          options: getFieldValues('domaine', inventaireType)
        },
        {
          name: 'nuisances-constatees',
          label: 'Nuisances constatées',
          type: 'multiselect',
          required: true,
          options: getFieldValues('nuisances-constatees', inventaireType)
        },
        {
          name: 'proximite-directe',
          label: 'Proximité directe',
          type: 'multiselect',
          required: true,
          options: getFieldValues('proximite-directe', inventaireType)
        },
        {
          name: 'dict',
          label: 'DICT',
          type: 'multiselect',
          required: false,
          options: getFieldValues('proximite-directe', inventaireType).filter((option: any) => ['eclairage-public', 'rseau-lectrique-gain', 'rseau-lectrique-non-gain', 'rseau-souterrain'].includes(option.id))
        },
        {
          name: 'taux-de-frquentation',
          label: 'Taux de fréquentation',
          type: 'radio',
          required: true,
          options: getFieldValues('taux-de-frquentation', inventaireType)
        },
        {
          name: 'type-de-passage',
          label: 'Type de passage',
          type: 'multiselect',
          required: true,
          options: getFieldValues('type-de-passage', inventaireType)
        },
        {
          name: 'accessibilite',
          label: 'Accessibilité',
          type: 'radio',
          required: true,
          options: getFieldValues('accessibilite', inventaireType)
        }
      ],
      category: 3
    },
    {
      id: 'preconisation-travaux',
      title: 'Travaux',
      description: 'Recommandations d\'intervention',
      fields: [
        {
          name: 'preco-travaux-abattage',
          label: 'Abattage',
          type: 'radio',
          required: true,
          options: getFieldValues('preco-travaux-abattage', inventaireType)
        },
        {
          name: 'preco-travaux-collet',
          label: 'Collet',
          type: 'multiselect',
          required: true,
          options: getFieldValues('preco-travaux-collet', inventaireType)
        },
        {
          name: 'preco-travaux-tronc',
          label: 'Tronc',
          type: 'multiselect',
          required: true,
          options: getFieldValues('preco-travaux-tronc', inventaireType)
        },
        {
          name: 'preco-travaux-houppier',
          label: 'Houppier',
          type: 'multiselect',
          required: true,
          options: getFieldValues('preco-travaux-houppier', inventaireType)
        },
        {
          name: 'preco-travaux-date-travaux',
          label: 'Echéance',
          type: 'radio',
          options: getFieldValues('preco-travaux-date-travaux', inventaireType)
        },

        {
          name: 'preco-travaux-com',
          label: 'Commentaires',
          type: 'textarea',
          placeholder: 'Ajoutez vos commentaires...'
        }
      ],
      category: 4
    },
    {
      id: 'estimation-valeur',
      title: 'Valeur BEVA',
      description: 'Évaluation de la valeur intrinsèque',
      fields: [
        {
          name: 'valeur-intrinseque-indice-espece',
          label: 'Indice espèce',
          type: 'number',
          placeholder: 'Entrez l\'indice espèce'
        },
        {
          name: 'valeur-intrinseque-indice-sanitaire',
          label: 'Indice sanitaire',
          type: 'select',
          options: [
            { id: 'vigoureux-sain', label: 'Vigoureux - Sain' },
            { id: 'vigoureux-malade', label: 'Vigoureux - Malade' },
            { id: 'vigoureux-deprissant', label: 'Vigoureux - Dépérissant' },
            { id: 'vigueur-moyenne-sain', label: 'Vigueur moyenne - Sain' },
            { id: 'vigueur-moyenne-malade', label: 'Vigueur moyenne - Malade' },
            { id: 'vigueur-moyenne-deprissant', label: 'Vigueur moyenne - Dépérissant' },
            { id: 'peu-vigoureux-sain', label: 'Peu vigoureux - Sain' },
            { id: 'peu-vigoureux-malade', label: 'Peu vigoureux - Malade' },
            { id: 'peu-vigoureux-deprissant', label: 'Peu vigoureux - Dépérissant' },
            { id: 'sans-vigueur-sain', label: 'Sans vigueur - Sain' },
            { id: 'sans-vigueur-malade', label: 'Sans vigueur - Malade' },
            { id: 'sans-vigueur-deprissant', label: 'Sans vigueur - Dépérissant' }
          ]
        },
        {
          name: 'valeur-intrinseque-indice-esthetique',
          label: 'Indice esthétique',
          type: 'select',
          options: [
            { id: 'remarquable-solitaire', label: 'Remarquable - Solitaire' },
            { id: 'remarquable-groupe2-5', label: 'Remarquable - Groupe < 5' },
            { id: 'remarquable-groupe5+', label: 'Remarquable - Alignement ou groupe > 5' },
            { id: 'beau-sujet-solitaire', label: 'Beau sujet - Solitaire' },
            { id: 'beau-sujet-groupe2-5', label: 'Beau sujet - Groupe < 5' },
            { id: 'beau-sujet-groupe5+', label: 'Beau sujet - Alignement ou groupe > 5' },
            { id: 'mal-forme-age-solitaire', label: 'Mal formé, âgé - Solitaire' },
            { id: 'mal-forme-age-groupe2-5', label: 'Mal formé, âgé - Groupe < 5' },
            { id: 'mal-forme-age-groupe5+', label: 'Mal formé, âgé - Alignement ou groupe > 5' },
            { id: 'sans-interet-solitaire', label: 'Sans intérêt - Solitaire' },
            { id: 'sans-interet-groupe2-5', label: 'Sans intérêt - Groupe < 5' },
            { id: 'sans-interet-groupe5+', label: 'Sans intérêt - Alignement ou groupe > 5' }
          ]
        },
        {
          name: 'valeur-intrinseque-indice-situation',
          label: 'Indice de situation',
          type: 'radio',
          options: getFieldValues('valeur-intrinseque-indice-situation', inventaireType)
        }
      ],
      category: 0
    }
  ];
}

/**
 * Default edit steps configuration for ARBRE type (for backward compatibility)
 */
export const ARBRE_EDIT_STEPS: Step[] = getEditSteps('ARBRE');
